import { Alert } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { difference } from "lodash-es";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { PersistentDrawer } from "@/components/Drawer/PersistentDrawer";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { AnimatedLoader } from "@/components/Loaders/AnimatedLoader";
import { PageLoader } from "@/components/Loaders/PageLoader";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import { useHotelDataContext } from "@/features/hotels/contexts/HotelDataContext";
import { useHotelSearchDataContext } from "@/features/hotels/contexts/HotelSearchDataContext";
import { calculateBookingPrice } from "@/features/hotels/utils/calculateBookingPrice";
import { TravellerSelectionStatusDrawer } from "@/features/travellers/components/TravellerSelectionStatusDrawer";
import TravellersList from "@/features/travellers/components/TravellersList";
import { TravellersSelection } from "@/features/travellers/components/TravellersSelection";
import { useTravellersSelectionContext } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import {
  getTravellerAgeAtTravelDate,
  getTravellerTypeAtTravelDate,
} from "@/features/travellers/utils/ageValidation";
import { travelerTypeToTypeCode } from "@/features/travellers/utils/transformTraveler";
import { useCreatePendingInstallment } from "@/hooks/useCreatePendingInstallment";
import { HotelTrackingEvents, rudderAnalytics } from "@/lib/rudderstack";
import type { FindUserTravelersTravelers } from "@/types/codegen";
import type { TravellerAgeGroup } from "@/types/types";

export const Route = createFileRoute(
  "/_authenticated/hotels/_hotels/_hotel-offer/$id/travelers",
)({
  component: RouteComponent,
  staticData: {
    hotelStep: { stepNumber: 2 },
    drawer: true,
  },
});

function RouteComponent() {
  const intl = useIntl();
  const navigate = Route.useNavigate();
  const params = Route.useParams();

  const { data: hotelData } = useHotelDataContext();
  const { travellerSlots, selectedTravellers, selectTraveller } =
    useTravellersSelectionContext();
  const { data: booking } = useHotelBookingContext();
  const { searchData } = useHotelSearchDataContext();
  const { data: travellers } = useQuery(travellersQueryOptions());

  const { paymentType, selectedInstallments, refreshSession } =
    usePaymentContext();

  const {
    mutateAsync: createPendingInstallment,
    isPending,
    isError: isCreatePendingInstallmentError,
  } = useCreatePendingInstallment();

  const [isEligibilityLoading, setIsEligibilityLoading] = useState(false);

  const handleContinue = async () => {
    if (!booking) {
      return;
    }

    const totalPrice = calculateBookingPrice(booking);

    rudderAnalytics.track(
      HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
      {
        bookingType: "Hotel",
        selected_offer_id: booking.offerId,
        price: totalPrice.value,
      },
    );

    try {
      if (paymentType === "bnpl") {
        setIsEligibilityLoading(true);
        try {
          const { data: eligibilityData } = await refreshSession();
          if (eligibilityData?.isEligible && totalPrice.value) {
            await createPendingInstallment({
              sessionId: eligibilityData.sessionId,
              amount: totalPrice.value,
              countOfMonth: selectedInstallments,
            });
          }
        } finally {
          setIsEligibilityLoading(false);
        }
      }
      navigate({ to: "/hotels/$id/summary" });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const [selectedSlot, setSelectedSlot] = useState<{
    ageGroup: TravellerAgeGroup;
    index: number;
  } | null>(null);

  const travelDate = useMemo(
    () => (searchData ? new Date(searchData.dates.checkIn) : null),
    [searchData],
  );

  const selectedChildrenAges = useMemo(() => {
    const selectedChildrenIds = travellerSlots.CHD.filter(Boolean);
    return selectedChildrenIds.map((id) => {
      const foundTraveller = travellers?.find(
        (traveller) => traveller.id === id,
      );
      if (!foundTraveller || !travelDate) return null;
      return getTravellerAgeAtTravelDate(foundTraveller.birthDate, travelDate);
    });
  }, [travelDate, travellerSlots.CHD, travellers]);

  if (!searchData || !travelDate || !hotelData) {
    Sentry.captureMessage(
      "User tried to access hotel travelers page directly without going through the hotel offer page",
      "fatal",
    );
    return <Navigate to="/hotels/$id/details" params={params} />;
  }

  if (isPending || isEligibilityLoading) {
    return (
      <PageLoader>
        <AnimatedLoader icon="buildings" />
        <h3 className="mt-3 font-normal text-text-primary">
          {intl.formatMessage({
            defaultMessage: "Checking room availability...",
            description:
              "Loading message shown while room availability is being checked",
          })}
        </h3>
      </PageLoader>
    );
  }

  const travellersToSelect =
    travellerSlots.ADT.length +
    travellerSlots.CHD.length +
    travellerSlots.INF.length;

  const allTravellersSelected =
    travellerSlots.ADT.every(Boolean) &&
    travellerSlots.CHD.every(Boolean) &&
    travellerSlots.INF.every(Boolean);

  const childrenAges = searchData.rooms.flatMap((room) => room.childrenAges);

  const isSelectionDisabled = (traveller: FindUserTravelersTravelers) =>
    selectedTravellers.some((t) => t.id === traveller.id) ||
    // for children, we only allow selecting travellers with ages that were selected in the search
    (["child", "infant"].includes(
      getTravellerTypeAtTravelDate(traveller.birthDate, travelDate),
    )
      ? !difference(childrenAges, selectedChildrenAges).includes(
          getTravellerAgeAtTravelDate(traveller.birthDate, travelDate),
        )
      : false);

  return (
    <div className="px-4">
      {isCreatePendingInstallmentError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {intl.formatMessage({
            defaultMessage:
              "Only one installment order is allowed per 24 hours. Please try again later.",
            description:
              "Error shown when a second instalment order is attempted within 24 hours",
          })}
        </Alert>
      )}

      <TravellersSelection
        onSlotClick={(ageGroup, index) => {
          setSelectedSlot({ ageGroup, index });
        }}
      />

      <SwipeableDrawer
        hasFullHeight
        heightOffset={24}
        open={!!selectedSlot}
        onClose={() => setSelectedSlot(null)}
        backgroundColor="var(--color-surface-background)"
      >
        <DrawerTopBar
          onCloseClick={() => setSelectedSlot(null)}
          title={
            selectedSlot
              ? intl.formatMessage(
                  {
                    defaultMessage: "Add traveller for {type}",
                    description:
                      "Prefix label when assigning a traveller to a booking slot",
                  },
                  {
                    type: intl.formatMessage(
                      {
                        defaultMessage:
                          "{type, select, ADT {Adult} CHD {Child} INF {Infant} other {Traveller}} {number}",
                        description:
                          "Numbered traveller slot label (e.g. 'Adult 1', 'Child 2')",
                      },
                      {
                        type: selectedSlot.ageGroup,
                        number: selectedSlot.index + 1,
                      },
                    ),
                  },
                )
              : null
          }
        />
        {selectedSlot ? (
          <TravellersList
            travellers={(travellers || []).filter(
              (traveller) =>
                travelerTypeToTypeCode(
                  getTravellerTypeAtTravelDate(traveller.birthDate, travelDate),
                ) === selectedSlot.ageGroup,
            )}
            travelDate={travelDate}
            selectedTravellerId={
              travellerSlots[selectedSlot.ageGroup][selectedSlot.index]
            }
            isSelectionDisabled={isSelectionDisabled}
            onTravellerSelected={(travellerId) => {
              selectTraveller(selectedSlot.index, travellerId);
              setSelectedSlot(null);
            }}
          />
        ) : null}
      </SwipeableDrawer>

      <TravellerSelectionStatusDrawer
        travellersSelected={selectedTravellers.length}
        travellersToSelect={travellersToSelect}
      />

      <PersistentDrawer>
        <Button
          fullWidth
          disabled={!allTravellersSelected}
          variant="contained"
          onClick={handleContinue}
        >
          {isEligibilityLoading
            ? intl.formatMessage({
                defaultMessage: "Loading",
                description:
                  "Generic loading indicator text shown while content is being fetched",
              })
            : intl.formatMessage({
                defaultMessage: "Continue",
                description: "Button to proceed to the next hotel booking step",
              })}
        </Button>
      </PersistentDrawer>
    </div>
  );
}
